import {LoadAllCountriesUseCase} from '51trips-frontend-core'
import {CreatedTripbook, InactiveCode, PackDeliveryStatus, PackTraveler, PackTrip, PrintedTripbook} from '../../domain/entities/B2BPackDetail.ts'
import {InvitationCodeRecord} from '../../domain/entities/InvitationCode.ts'
import {TripbookPackage} from '../../domain/entities/Agency.ts'
import {TripbookFeedback} from '../../domain/entities/TripbookFeedback.ts'
import {CountryRepository} from '../../repositories/CountryRepository.ts'
import CountryISOCode from '../../utils/CountryISOCode.ts'
import {CodeLifecycle} from './CodeStatusPill.tsx'

const loadAllCountriesUseCase = new LoadAllCountriesUseCase(new CountryRepository())

// Shared between the "TripBooks impresos" column and the side panel's
// shipping section — both surface the same PackDelivery.status enum.
export const DELIVERY_LABEL_KEY: Record<PackDeliveryStatus, string> = {
    IN_PRODUCTION: 'packs.deliveryInProduction',
    IN_SHIPPING: 'packs.deliveryInShipping',
    DELIVERED: 'packs.statusDelivered',
    INCIDENT: 'packs.deliveryIncident',
}

// One row shape for the whole pack — printed, created and inactive codes side
// by side, ordered by how far each has progressed, matching the design's
// single "codes" table instead of three separate ones.
export type UnifiedCodeRow = {
    key: string
    codeId: string | null
    code: string | null
    lifecycle: CodeLifecycle
    traveler: PackTraveler | null
    trip: PackTrip | null
    tripId: string | null
    tripbookId: string | null
    deliverableUrl: string | null
    flipbookUrl: string | null
    feedback: TripbookFeedback | null
    delivery: {status: PackDeliveryStatus; trackingUrl: string | null; orderedAt: string | null} | null
}

// A code is "free" (disponible) while nothing has been written onto it — that's
// what "Asignar TripBook" consumes before minting a new one. A trip alone is
// enough to reserve it: traveler data is optional, and "un TripBook diferente
// para cada viajero" / "el mismo TripBook impreso" by the agency never write one,
// so a traveler-only check would hand an assigned code back to the pool (and
// the next assignment would overwrite its trip).
export const hasTraveler = (traveler: PackTraveler | null) => !!(traveler?.name || traveler?.email || traveler?.phone)

export const isReservedRow = (row: UnifiedCodeRow) => hasTraveler(row.traveler) || !!row.trip

export const isFreeCodeRow = (row: UnifiedCodeRow) => row.lifecycle === 'NOT_ASSIGNED' && !isReservedRow(row)

// --- Consumo del pack -------------------------------------------------------
// Una unidad se gasta en cuanto el código queda reservado para un viajero
// concreto: pendiente de entrega, entregado, con TripBook creado o ya
// impreso. Sólo un código todavía en blanco (sin viajero ni viaje) sigue disponible —
// ver isFreeCodeRow — porque ese es el único que "Asignar código a un
// viajero" puede reutilizar sin tocar la cuota del pack.
const CONSUMING_LIFECYCLES: CodeLifecycle[] = ['DELIVERED', 'TRIP_CREATED', 'PRINTED']

export const consumesPackUnit = (row: UnifiedCodeRow) =>
    CONSUMING_LIFECYCLES.includes(row.lifecycle) || (row.lifecycle === 'NOT_ASSIGNED' && isReservedRow(row))

// Unidades gastadas. En un código genérico (unlimitedRedemption) cada viaje ES
// la unidad: un mismo viajero puede tener varios viajes con el enlace, y cada
// uno gasta una unidad, así que se cuenta por tripId — nunca por email, que
// juntaría viajes distintos de la misma persona en uno solo. Para un código
// único se dedupe por codeId: el mismo código puede aparecer en más de un
// estado (creado e impreso) y es un solo libro. Ese libro gasta tantas unidades
// como copias se imprimen — un "Álbum compartido" de 5 copias son 5 unidades.
//
// Las filas sin identidad de agrupación (sin tripId en un genérico, sin
// código/codeId en un único) cuentan una a una: agruparlas todas bajo "sin
// identidad" sería el fallo caro. Mejor pasarse contando que regalar unidades.
export function countConsumedUnits(
    rows: UnifiedCodeRow[],
    recordsById: Map<string, InvitationCodeRecord>,
    genericCodeIds?: Set<string>,
): number {
    const genericByTrip = new Set<string>()
    let genericWithoutTrip = 0
    const uniqueByCode = new Map<string, number>()
    let uniqueWithoutCode = 0
    for (const row of rows) {
        if (!consumesPackUnit(row)) continue
        if (row.codeId && genericCodeIds?.has(row.codeId)) {
            if (row.tripId) genericByTrip.add(row.tripId)
            else genericWithoutTrip++
        } else {
            const codeKey = row.codeId ?? row.code
            const copies = Math.max(1, (row.codeId ? recordsById.get(row.codeId)?.copies : undefined) ?? 1)
            if (codeKey) uniqueByCode.set(codeKey, copies)
            else uniqueWithoutCode += copies
        }
    }
    let uniqueUnits = uniqueWithoutCode
    for (const copies of uniqueByCode.values()) uniqueUnits += copies
    return genericByTrip.size + genericWithoutTrip + uniqueUnits
}

// Lo que ve la agencia en el banner. Se calcula restando sobre las unidades
// contratadas y no contando códigos libres, porque una agencia con código
// genérico no tiene un pool de códigos que contar: tiene un enlace reusable y
// un cupo. Nunca baja de 0 — si hay sobreconsumo, el banner marca 0 y el botón
// de asignar se apaga, en vez de enseñar un negativo.
export function availablePackUnits(
    totalUnits: number,
    rows: UnifiedCodeRow[],
    recordsById: Map<string, InvitationCodeRecord>,
    genericCodeIds?: Set<string>,
): number {
    return Math.max(0, totalUnits - countConsumedUnits(rows, recordsById, genericCodeIds))
}

// --- Live overlay -----------------------------------------------------------
// printed/created rows come from the B2B metrics endpoint (BigQuery-backed, so
// it lags a write by however long the ETL takes). Codes that haven't produced a
// TripBook yet, though, are owned by GET /invitation-codes, which is live and
// gets invalidated on every write. Overlaying that record onto those rows makes
// an assignment show up in the table as soon as it's saved instead of whenever
// the ETL next runs.

let countryTitlesById: Map<string, string> | null = null

function countryTitle(countryId: string): string {
    if (!countryTitlesById) {
        countryTitlesById = new Map(loadAllCountriesUseCase.loadAll().map((c) => [c.id, c.title]))
    }
    return countryTitlesById.get(countryId) ?? countryId
}

// PackTrip dates are plain YYYY-MM-DD (formatDate splits on '-'), and the
// itinerary epochs are written as UTC midnight, so read them back in UTC.
function toPackDate(epochMs?: number): string | null {
    return epochMs == null ? null : new Date(epochMs).toISOString().slice(0, 10)
}

function toPackTraveler(record: InvitationCodeRecord): PackTraveler {
    const name = [record.mainTraveler?.firstName, record.mainTraveler?.lastName].filter(Boolean).join(' ')
    return {
        name: name || null,
        email: record.mainTraveler?.email ?? null,
        phone: record.mainTraveler?.phone ?? null,
    }
}

function toPackTrip(record: InvitationCodeRecord): PackTrip | null {
    const itinerary = record.itinerary
    if (!itinerary?.countryCodes?.length && itinerary?.startDate == null) return null
    return {
        countries: (itinerary?.countryCodes ?? []).map((id) => ({
            name: countryTitle(id),
            iso2: CountryISOCode.fromCode(id)?.alpha2Code ?? id,
        })),
        startDate: toPackDate(itinerary?.startDate),
        endDate: toPackDate(itinerary?.endDate),
    }
}

function toUnifiedRow(record: InvitationCodeRecord): UnifiedCodeRow {
    return {
        key: `inactive-${record.id}`,
        codeId: record.id,
        code: record.code ?? null,
        lifecycle: record.isDelivered ? 'DELIVERED' : 'NOT_ASSIGNED',
        traveler: toPackTraveler(record),
        trip: toPackTrip(record),
        tripId: null,
        tripbookId: null,
        // Only the metrics response knows the minted deliverable, so a code the
        // ETL hasn't seen yet shows a dash in "Entregable" until it catches up.
        deliverableUrl: null,
        flipbookUrl: null,
        feedback: null,
        delivery: null,
    }
}

export function unifyCodeRows(
    printed: PrintedTripbook[],
    created: CreatedTripbook[],
    inactive: InactiveCode[],
    // Live invitation codes by id. When given, they take precedence over the
    // metrics data for codes with no TripBook yet, and codes the metrics
    // response doesn't know about at all are added as new rows.
    liveCodes?: Map<string, InvitationCodeRecord>,
    // Reusable agency-wide codes, which belong in the invitation-link banner
    // and must never be materialised as a row here.
    excludeCodeIds?: Set<string>,
): UnifiedCodeRow[] {
    const printedRows: UnifiedCodeRow[] = printed.map((p) => ({
        key: `printed-${p.tripId}`,
        codeId: p.codeId,
        code: p.code,
        lifecycle: 'PRINTED',
        traveler: p.traveler,
        trip: p.trip,
        tripId: p.tripId,
        tripbookId: p.tripbookId,
        deliverableUrl: null,
        flipbookUrl: p.flipbookUrl,
        feedback: p.feedback,
        delivery: p.delivery,
    }))

    const createdRows: UnifiedCodeRow[] = created.map((c) => ({
        key: `created-${c.tripId}`,
        codeId: c.codeId,
        code: c.code,
        lifecycle: 'TRIP_CREATED',
        traveler: c.traveler,
        trip: c.trip,
        tripId: c.tripId,
        tripbookId: c.tripbookId,
        deliverableUrl: c.deliverableUrl,
        flipbookUrl: null,
        feedback: c.feedback,
        delivery: null,
    }))

    let inactiveRows: UnifiedCodeRow[] = inactive.map((i) => ({
        key: `inactive-${i.codeId}`,
        codeId: i.codeId,
        code: i.code,
        lifecycle: i.status === 'DELIVERED' ? 'DELIVERED' : 'NOT_ASSIGNED',
        traveler: i.traveler,
        trip: i.trip,
        tripId: null,
        tripbookId: null,
        deliverableUrl: i.deliverableUrl,
        flipbookUrl: null,
        feedback: null,
        delivery: null,
    }))

    if (liveCodes?.size) {
        // The live record wins for traveler/trip/delivered on codes with no
        // TripBook yet — printed/created rows keep the metrics data, which
        // carries the tripbook, shipment and feedback the record doesn't have.
        //
        // A code with an id but no live record is gone from GET /invitation-codes
        // for this agency — cancelled (see cancelInvitationCode, which drops it
        // from that endpoint) or deleted outright — so the stale BigQuery/ETL row
        // for it must be dropped too, not just left unchanged: otherwise a
        // cancelled code keeps showing here until the next ETL run ever catches
        // up, which for a pack agency's inactive list can be indefinitely long.
        inactiveRows = inactiveRows
            .filter((row) => !row.codeId || liveCodes.has(row.codeId))
            .map((row) => {
                const record = row.codeId ? liveCodes.get(row.codeId) : undefined
                if (!record) return row
                return {
                    ...row,
                    code: record.code ?? row.code,
                    lifecycle: record.isDelivered ? 'DELIVERED' : 'NOT_ASSIGNED',
                    traveler: toPackTraveler(record),
                    trip: toPackTrip(record) ?? row.trip,
                }
            })

        // Match on the code string as well as the id: printed/created rows have a
        // nullable codeId, and a redeemed code whose id the ETL didn't resolve
        // would otherwise reappear here as a phantom "disponible" row.
        const allRows = [...printedRows, ...createdRows, ...inactiveRows]
        const knownCodeIds = new Set(allRows.map((row) => row.codeId).filter(Boolean))
        const knownCodes = new Set(allRows.map((row) => row.code).filter(Boolean))
        const unseen = Array.from(liveCodes.values()).filter((record) =>
            !knownCodeIds.has(record.id)
            && !(record.code && knownCodes.has(record.code))
            && !record.unlimitedRedemption
            && !excludeCodeIds?.has(record.id)
        )
        inactiveRows = [...inactiveRows, ...unseen.map(toUnifiedRow)]
    }

    // Most-progressed first: printed, then trip-created, then delivered, then not-assigned.
    inactiveRows.sort((a, b) => Number(b.lifecycle === 'DELIVERED') - Number(a.lifecycle === 'DELIVERED'))

    // Defensive dedupe by key: pooling every pack into one list (no more
    // per-pack grouping) means an upstream duplicate — e.g. the same pack
    // listed twice — now shows up as a repeated row instead of a repeated,
    // easy-to-miss section.
    const seen = new Set<string>()
    return [...printedRows, ...createdRows, ...inactiveRows].filter((row) => {
        if (seen.has(row.key)) return false
        seen.add(row.key)
        return true
    })
}

// --- Group albums ------------------------------------------------------------
// "Varios TripBooks" (AssignInvitationCodeDialog) leaves two shapes behind:
//   - Grupo - Álbum individual: N codes sharing a groupId, one per traveler.
//     Tables show them as one collapsible item with the codes as sub-rows.
//   - Grupo - Álbum compartido: one code with copies > 1 (one book, printed N
//     times). Still one row, flagged as a group, with a traveler list.

export type CodeGroupKind = 'individual' | 'shared'

export type CodeTableItem =
    | {kind: 'row'; key: string; row: UnifiedCodeRow}
    | {kind: 'group'; key: string; rows: UnifiedCodeRow[]}

export const isSharedAlbum = (record?: InvitationCodeRecord) => (record?.copies ?? 1) > 1

export const codeGroupKind = (record?: InvitationCodeRecord): CodeGroupKind | null =>
    record?.groupId ? 'individual' : isSharedAlbum(record) ? 'shared' : null

// Design order: per-traveler groups first, then shared albums, then single
// codes — each bucket keeping the order the rows came in.
export function groupCodeRows(rows: UnifiedCodeRow[], recordsById: Map<string, InvitationCodeRecord>): CodeTableItem[] {
    const groups = new Map<string, UnifiedCodeRow[]>()
    const shared: UnifiedCodeRow[] = []
    const singles: UnifiedCodeRow[] = []
    for (const row of rows) {
        const record = row.codeId ? recordsById.get(row.codeId) : undefined
        if (record?.groupId) groups.set(record.groupId, [...(groups.get(record.groupId) ?? []), row])
        else if (isSharedAlbum(record)) shared.push(row)
        else singles.push(row)
    }
    return [
        ...Array.from(groups, ([groupId, members]): CodeTableItem => ({kind: 'group', key: `group-${groupId}`, rows: members})),
        ...[...shared, ...singles].map((row): CodeTableItem => ({kind: 'row', key: row.key, row})),
    ]
}

// --- Paginación -------------------------------------------------------------
// Packs can come in different paginations (TripbookPackage.numberOfPagesIncluded,
// falling back to the agency's). The assign dialog lets the agency pick one, so
// each option needs its own stock: units bought at those pages minus codes
// already spent at those pages. A code that predates storing its pages counts
// as the agency's default.

export type PageOption = {pages: number; available: number}

export function pageOptionsFor(
    packages: Pick<TripbookPackage, 'quantity' | 'numberOfPagesIncluded'>[],
    agencyPages: number,
    rows: UnifiedCodeRow[],
    recordsById: Map<string, InvitationCodeRecord>,
    genericCodeIds?: Set<string>,
): PageOption[] {
    const unitsByPages = new Map<number, number>()
    for (const pkg of packages) {
        const pages = pkg.numberOfPagesIncluded ?? agencyPages
        unitsByPages.set(pages, (unitsByPages.get(pages) ?? 0) + pkg.quantity)
    }
    const pagesOf = (row: UnifiedCodeRow) => (row.codeId ? recordsById.get(row.codeId)?.numberOfPagesIncluded : undefined) ?? agencyPages
    return Array.from(unitsByPages, ([pages, units]) => ({
        pages,
        available: Math.max(0, units - countConsumedUnits(rows.filter((row) => pagesOf(row) === pages), recordsById, genericCodeIds)),
    })).sort((a, b) => a.pages - b.pages)
}
